const CLARITY_MAX_TAG_LENGTH = 255;
const CLARITY_RETRY_DELAYS_MS = [500, 1500, 3000] as const;

type ClarityFn = (...args: unknown[]) => void;

function getClarity(): ClarityFn | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const clarity = (window as Window & { clarity?: ClarityFn }).clarity;
  return typeof clarity === 'function' ? clarity : null;
}

function truncate(value: string, max = CLARITY_MAX_TAG_LENGTH): string {
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, max - 3)}...`;
}

export interface ClarityErrorReport {
  /** Custom tag + event name in Clarity (e.g. `event-page-error`). */
  errorType: string;
  error: Error & { digest?: string };
  context?: string;
}

function buildTags(
  errorType: string,
  error: Error & { digest?: string },
  context?: string,
): Record<string, string> {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const eventSlug = pathname.match(/^\/events\/([^/]+)/)?.[1] ?? '';

  return {
    errorType,
    errorMessage: truncate(error.message || 'Unknown error'),
    ...(error.digest ? { errorDigest: error.digest } : {}),
    ...(context ? { errorContext: truncate(context) } : {}),
    pagePath: truncate(pathname),
    ...(eventSlug ? { eventSlug: truncate(eventSlug, 128) } : {}),
  };
}

function sendToClarity(tags: Record<string, string>, eventName: string): boolean {
  const clarity = getClarity();
  if (!clarity) {
    return false;
  }

  try {
    Object.entries(tags).forEach(([key, value]) => {
      clarity('set', key, value);
    });
    clarity('event', eventName);
    return true;
  } catch {
    return false;
  }
}

/**
 * Reports a caught error to Microsoft Clarity via custom tags and a custom event.
 * Retries briefly because the Clarity script loads with `lazyOnload`.
 */
export function reportErrorToClarity({
  errorType,
  error,
  context,
}: ClarityErrorReport): void {
  const tags = buildTags(errorType, error, context);

  if (sendToClarity(tags, errorType)) {
    return;
  }

  CLARITY_RETRY_DELAYS_MS.forEach((delay) => {
    window.setTimeout(() => {
      sendToClarity(tags, errorType);
    }, delay);
  });
}
